import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';

import pois from '../../static/pois.geo.json';

import {makeStyles, withTheme} from '@material-ui/core/styles';
import {useMediaQuery, Box, Typography, Avatar} from '@material-ui/core';

import {ResponsiveHeader, SidePanel, Map, SwitchPad} from '@geomatico/geocomponents';

import {
  WIDESCREEN_STEP,
  INITIAL_VIEWPORT,
  DRAWER_WIDTH,
  MODES, SITES, SOURCES, MAP_LEGEND, MAPSTYLEURL
} from '../config';
import Logo from '../components/icons/Logo';

import {Marker, Popup} from 'react-map-gl';
import SectionTitle from '../components/SectionTitle';
import SiteList from '../components/SiteList';
import {DirectionsBike, DirectionsWalk} from '@material-ui/icons';
import Legend from '../components/Legend';

import './marker.css';

const useStyles = makeStyles((theme) => ({
  sidebar: {
    height: '90vh'
  },
  mapContent: {
    flexGrow: 1,
    padding: 0,
    position: 'absolute',
    top: theme.mixins.toolbar.minHeight,
    bottom: 0,
    right: 0,
    left: ({isSidePanelOpen, widescreen}) => isSidePanelOpen && widescreen ? DRAWER_WIDTH : 0,
  },
  layerlabel: {
    letterSpacing: 1.05,
    paddingLeft: 25,
  },
  legend: {
    fontSize: 12,
    marginLeft: 22
  },
  legendIcon: {
    marginLeft: 25,
  }
}));

const App = ({theme}) => {
  const widescreen = useMediaQuery(`(min-width: ${WIDESCREEN_STEP})`, {noSsr: true});
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(true);
  const [viewport, setViewport] = useState(INITIAL_VIEWPORT);
  const classes = useStyles({isSidePanelOpen, widescreen});

  const [mode, setMode] = useState();
  const [site, setSite] = useState();
  const [tooltipInfo, setTooltipInfo] = useState();

  const handleSiteModeChange = ({site, mode}) => {
    setSite(site);
    setMode(mode);
  };

  const handleSiteClick = (newSite) => {
    setSite(site === newSite ? undefined : newSite);
    setMode(mode ? mode : 'foot');
  };

  const toggleMode = () => setMode(mode === 'foot' ? 'cycling' : 'foot');

  const poiTypes =  useMemo(() => MAP_LEGEND.map(entry => ({
    id: entry.id,
    label: <>
      <Typography component='span' variant='body2' color='textSecondary' style={{padding: '0 10px'}}>{entry.name}</Typography>
      {entry.icon}
    </>,
    color: theme.palette.grey[800]
  }))
  , []);

  const [selectedPoiTypes, selectPoiTypes] = useState(poiTypes.map(type => type.id));

  const onViewportChange = (viewport) =>
    setViewport({
      ...viewport
    });

  const handleClose = () => setIsSidePanelOpen(!isSidePanelOpen);
  const getIcon = (area) => MAP_LEGEND.find(({id}) => id === area)?.icon;

  const poisComponents = useMemo(() => ([
    ...pois.features
      .filter(feature => selectedPoiTypes.includes(feature.properties.area))
      .map((feature, i) =>
        <Marker
          key={i}
          captureDrag={false}
          latitude={feature.geometry.coordinates[1]}
          longitude={feature.geometry.coordinates[0]}
          offsetLeft={-9} offsetTop={-9}
          onClick={() => setTooltipInfo(feature)}>
          {getIcon(feature.properties.area)}
        </Marker>),
    ...SITES.map(site =>
      <Marker
        key={site.id}
        captureDrag={false}
        latitude={site.lat}
        longitude={site.lon}
        offsetLeft={-20} offsetTop={-20}>
        <Avatar style={{backgroundColor: '#FFFC', border: '1px solid #0003', filter: 'drop-shadow(2px 2px 1px rgba(0,0,0,.2))'}} onClick={() => handleSiteClick(site.id)}>
          {site.icon}
        </Avatar>
      </Marker>)
  ]),[selectedPoiTypes, site, mode]);
  
  const layers = useMemo(() => (mode && site ?
    [{
      'id': 'isochrone-areas',
      'source': `donut-${mode}-${site}`,
      'type': 'fill',
      'paint': {
        'fill-opacity': 0.17,
        'fill-color': [
          'match',
          ['get', 'value'],
          300, '#00ff00',
          600, '#ffff00',
          900, '#ff0000',
          '#ff00ff'
        ]
      }
    }, {
      'id': 'isochrone-limits',
      'source': `outer-${mode}-${site}`,
      'type': 'line',
      'paint': {
        'line-width': 2,
        'line-opacity': 0.7,
        'line-color': [
          'match',
          ['get', 'value'],
          300, '#00aa00',
          600, '#aaaa00',
          900, '#aa0000',
          '#aa00aa'
        ]
      }
    }, {
      'id': 'isochrone-labels',
      'source': `outer-${mode}-${site}`,
      'type': 'symbol',
      'minzoom': 13,
      'layout': {
        'symbol-placement': 'line',
        'text-font': ['Open Sans Regular'],
        'text-field': ['concat', ['/', ['get', 'value'], 60], ' min'],
        'text-size': 12,
        'text-offset': [0, -0.5]
      },
      'paint': {
        'text-color': [
          'match',
          ['get', 'value'],
          300, '#00aa00',
          600, '#aaaa00',
          900, '#aa0000',
          '#aa00aa'
        ]
      }
    }]
    : []
  ), [mode, site]);

  return (
    <>
      <ResponsiveHeader
        logo={<Logo/>}
        logoStyleProps={{
          width: 170,
          paddingTop: 0,
        }}
        onStartIconClick={widescreen ? undefined : handleClose}
      >
      </ResponsiveHeader>
      <SidePanel
        drawerWidth={DRAWER_WIDTH}
        anchor="left"
        isOpen={isSidePanelOpen}
        onClose={handleClose}
        widescreen={widescreen}
      >
        <div>
          <Box mb={6} display='flex' flexDirection='column' justifyContent='space-between'>
            <div>
              <img style={{maxWidth: '100%'}} src='images/cabecera.png' alt='Pinto, ciudad de los 15 minutos'/>
              <Typography variant='body1' color='textSecondary' style={{padding: 8, textAlign: 'justify'}}>
                Selecciona un botón para ver hasta dónde puedes llegar
                a pie <DirectionsWalk fontSize='inherit'/> o en bicicleta <DirectionsBike fontSize='inherit'/> desde
                alguno de los cuatro lugares que te proponemos.
              </Typography>
            </div>
            <div>
              <SectionTitle title='Tiempos desde...'/>
              <SiteList sites={SITES} modes={MODES} selectedSite={site} selectedMode={mode} onChange={handleSiteModeChange} />
            </div>
            <div>
              <SectionTitle title='Puntos de interés'/>
              <SwitchPad categories={poiTypes} selected={selectedPoiTypes} onSelectionChange={selectPoiTypes} />
            </div>
          </Box>
        </div>
      </SidePanel>
      <main className={classes.mapContent}>
        <Map
          mapStyle={MAPSTYLEURL}
          viewport={viewport}
          onViewportChange={onViewportChange}
          sources={SOURCES}
          layers={layers}
          onClick={() => setTooltipInfo()}
        >
          {viewport.zoom > 12 && poisComponents}
          {tooltipInfo && (
            <Popup
              tipSize={5}
              anchor="bottom"
              latitude={tooltipInfo.geometry.coordinates[1]}
              longitude={tooltipInfo.geometry.coordinates[0]}
              closeOnClick={true}
              closeButton={false}
              onClose={() => setTooltipInfo()}
              offsetTop={-9}
            >
              {tooltipInfo.properties.area === 'bus' ?
                <>
                  <Typography variant='body2'>{tooltipInfo.properties.direccion}</Typography>
                  <Typography variant='caption' style={{fontWeight: 800}}>{tooltipInfo.properties.nombre}</Typography>
                </> :
                <Typography variant='body2'>{tooltipInfo.properties.nombre}</Typography>
              }
            </Popup>
          )}
        </Map>
        {mode && site && <Legend
          icon={MODES.find(({id}) => id === mode).icon}
          modeLabel={MODES.find(({id}) => id === mode).label}
          siteLabel={SITES.find(({id}) => id === site).label}
          onClick={toggleMode}
        />
        }
      </main>
    </>
  );
};

App.propTypes = {
  theme: PropTypes.object.isRequired
};

export default withTheme(App);
